import classNames from 'classnames';
import React, { useState } from 'react';
import ReactSelect, { Props as ReactSelectProps } from 'react-select';

import { ChevronDown } from '../../Icon/index.js';
import Typography from '../../Typography/index.js';

import { generateRandomId } from '../../../utils/random.js';

export interface SelectProps
  extends Omit<
    ReactSelectProps,
    'isDisabled' | 'isMulti' | 'isClearable' | 'isSearchable'
  > {
  /**
   * If true, the input will be disabled.
   * @default false
   */
  disabled?: boolean;

  /**
   * An optional error message to show to the user.
   */
  error?: string;

  /**
   * An optional label to pass to the input element.
   */
  label?: string;

  /**
   * The placeholder message to display.
   * @default "Select an option"
   */
  placeholder?: string;

  /**
   * If true, the input and font-size will be smaller.
   * @default false
   */
  small?: boolean;
}

/**
 * Select is associated with dropdowns that have a single selection that
 * populates the value once selected. Select fields show a dropdown upon focus,
 * which should initiate 4px below the input.
 *
 * @see https://www.figma.com/file/LdhxwbVqKRRLpyK5ekr4Q8/Component-Library?node-id=1921%3A1776&t=ou8UzyaGptd0CInG-0
 */
const Select = React.forwardRef(
  (
    {
      className,
      disabled = false,
      error,
      label,
      placeholder = 'Select an option',
      small = false,
      ...rest
    }: SelectProps,
    ref: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const hasError = Boolean(error);

    const [errorId] = useState(generateRandomId());

    const Error = error ? (
      <Typography
        as="span"
        color="danger"
        variant={small ? 'paragraph-small' : 'paragraph-medium'}
        id={errorId}
      >
        {error}
      </Typography>
    ) : null;

    const Input = (
      <ReactSelect
        aria-invalid={hasError}
        aria-errormessage={hasError ? errorId : undefined}
        className={'select'}
        classNames={{
          control: () => (hasError ? 'control-error' : 'control'),
          indicatorsContainer: () => 'indicators-container',
          menu: () => 'select-menu',
          option: () => 'select-option',
          placeholder: () => 'select-placeholder',
          singleValue: () => 'single-value',
          valueContainer: () => 'value-container',
        }}
        isDisabled={disabled}
        placeholder={placeholder}
        components={{
          DropdownIndicator: () => <ChevronDown size="large" />,
          IndicatorSeparator: () => null,
        }}
        {...rest}
      />
    );

    if (label) {
      return (
        <div className={classNames('select-wrapper', className)} ref={ref}>
          <Typography
            as="label"
            bold
            className="select-label"
            variant={small ? 'title-medium' : 'title-large'}
          >
            {label}
            {Input}
          </Typography>
          {Error}
        </div>
      );
    }

    return (
      <div className={classNames('select-wrapper', className)} ref={ref}>
        {Input}
        {Error}
      </div>
    );
  },
);

Select.displayName = 'Select';

export default Select;
